import {
    Fragment,
    type ReactNode,
    type UIEvent,
    useEffect,
    useRef,
    useState,
} from 'react'
import {Loading} from 'swayok-react-mdb-ui-kit/components/Loading/Loading'
import {trans} from '../../../../helpers'
import type {ChatMessageData} from '../ChatsModuleTypes'
import {ChatBehaviorWarning} from './ChatBehaviorWarning'
import {ChatMessagesListItem} from './ChatMessagesListItem'

interface Props {
    // В мини-чате сообщения всегда в мобильной раскладке: окно узкое даже на десктопе.
    miniChat?: boolean
    messages: ChatMessageData[]
    isAllMessagesLoaded: boolean
    isLoadingMoreMessages: boolean
    // Вызывается, когда прокрутка чата достигает
    // барьера для догрузки более старых сообщений.
    loadMoreMessages: () => void
    // Вызывается при прокрутке чата.
    onScroll?: () => void
    Prepend: ReactNode | ReactNode[]
}

let isScrollEventsAllowed: boolean = true
const scrollEventsThrottling: number = 50

// Список сообщений в чате.
export function ChatMessagesList(props: Props) {

    const {
        miniChat,
        messages,
        isAllMessagesLoaded,
        isLoadingMoreMessages,
        loadMoreMessages,
        onScroll: onScroll,
        Prepend,
    } = props

    // ID Последнего сообщения в списке.
    const [
        lastId,
        setLastId,
    ] = useState<null | number>(null)
    // Индикатор готовности для отложенных эффектов.
    const [
        isMounted,
        setIsMounted,
    ] = useState<boolean>(false)
    // Высота и смещение полосы прокрутки перед запуском загрузки следующей пачки сообщений.
    const [
        scrollHeight,
        setScrollHeight,
    ] = useState<number | null>(null)

    // Контейнер сообщений с прокруткой.
    const scrollableRef = useRef<HTMLDivElement>(null)

    // Прокрутка в конец списка сообщений.
    const scrollToBottom = (smooth: boolean) => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo({
                top: scrollableRef.current.scrollHeight,
                left: 0,
                behavior: smooth ? 'smooth' : undefined,
            })
        }
    }

    // При монтировании компонента: отложить активацию отслеживания прокрутки.
    useEffect(() => {
        setTimeout(() => setIsMounted(true), 1000)
    }, [])

    // Определение последнего ID в списке сообщений.
    // При добавлении нового сообщения: lastId изменится и активируется прокрутка
    // вниз списка сообщений.
    // При этом прокрутка не активируется, если сообщения добавляются в начало списка.
    // Также отрабатывает при монтировании компонента.
    useEffect(() => {
        if (messages.length > 0) {
            // Запрещаем обработку событий прокрутки.
            isScrollEventsAllowed = false
            setTimeout(() => {
                isScrollEventsAllowed = true
                // Фокусируемся на контейнере, чтобы в мини-чате не скролилась страница
                // вместо сообщений, когда открывается чат. Только при открытии:
                // после отправки фокус должен остаться в поле ввода.
                if (lastId === null) {
                    scrollableRef.current?.focus()
                }
            }, 500)
            const newLastId: number = messages[messages.length - 1].id
            if (newLastId !== lastId) {
                // Добавлены сообщения в конец списка.
                setLastId(newLastId)
                // Прокрутка вниз при изменении lastId.
                // Задержка нужна для отрисовки сообщений.
                setTimeout(() => scrollToBottom(lastId !== null), 150)
            }

        }
    }, [messages])

    // Восстановление позиции полосы прокрутки после добавления сообщений в начало списка.
    // Отрабатывает, если полоса прокрутки прилеплена к верху контейнера.
    useEffect(() => {
        if (
            !isLoadingMoreMessages
            && scrollHeight
        ) {
            if (
                scrollableRef.current
                && scrollableRef.current.scrollHeight > scrollHeight
                && scrollableRef.current.scrollTop === 0
            ) {
                scrollableRef.current.scrollTo({
                    top: scrollableRef.current.scrollHeight - scrollHeight,
                })
            }
            setScrollHeight(null)
        }
    }, [isLoadingMoreMessages])

    // Автозагрузка сообщений при прокрутке.
    const handleScrolling = (event: UIEvent<HTMLDivElement>) => {
        if (!isMounted) {
            return
        }
        if (
            isScrollEventsAllowed
            && !isLoadingMoreMessages
            && !isAllMessagesLoaded
        ) {
            // Предотвращаем спам событиями.
            isScrollEventsAllowed = false
            setTimeout(() => {
                isScrollEventsAllowed = true
            }, scrollEventsThrottling)
            const minScrollTop = Math.min(
                300,
                event.currentTarget.scrollHeight * 0.4
            )
            if (event.currentTarget.scrollTop < minScrollTop) {
                setScrollHeight(event.currentTarget.scrollHeight)
                event.currentTarget.scrollTo({top: 5})
                loadMoreMessages()
            } else {
                onScroll?.()
            }
        } else if (
            !isAllMessagesLoaded
            && event.currentTarget.scrollTop <= 5
        ) {
            // Хак, чтобы браузер не прилеплял полосу прокрутки к верху контейнера.
            // Если прокрутка не прилеплена к верху контейнера, то, при добавлении
            // сообщений в начало списка, полоса прокрутки останется на правильном месте.
            event.currentTarget.scrollTo({top: 5})
        }
    }

    return (
        <div
            className="chat-messages-scroll pt-3 px-3 px-sm-4 flex-1 overflow-x-hidden overflow-y-auto"
            ref={scrollableRef}
            onScroll={handleScrolling}
        >
            {Prepend}
            <Loading
                loading={isLoadingMoreMessages}
                floating
            />
            <ChatBehaviorWarning miniChat={miniChat} />
            {messages.map((message: ChatMessageData, index: number) => {
                let date: string | null = message.createdAtDate
                const isNewDay: boolean = !!date && (
                    index === 0
                    || date !== messages[index - 1].createdAtDate
                )
                if (isNewDay && message.createdAtRecently) {
                    switch (message.createdAtRecently) {
                        case 'today':
                            date = trans().cabinet.chats.created_recently.today
                            break
                        case 'yesterday':
                            date = trans().cabinet.chats.created_recently.yesterday
                            break
                    }
                }
                return (
                    <Fragment key={'message-' + message.id}>
                        {isNewDay && (
                            <div className="text-center text-gray fs-7 fw-800 mb-3">
                                {date}
                            </div>
                        )}
                        <ChatMessagesListItem
                            message={message}
                            miniChat={miniChat}
                        />
                    </Fragment>
                )
            })}
        </div>
    )
}
